import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Gallery images - replace placeholders with actual images
const GALLERY_IMAGES = [
  {
    id: 1,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/o51c66q4_kitchen.jpg",
    alt: "Modern kitchen installation",
    category: "Kitchens",
  },
  {
    id: 2,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/nlxhapnp_kitchen2.jpg",
    alt: "Blue modern kitchen",
    category: "Kitchens",
  },
  {
    id: 3,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/fp08lgvs_extensions.jpg",
    alt: "Home extension build",
    category: "Extensions",
  },
  {
    id: 4,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/uj89j5hp_extension2.jpg",
    alt: "Extension interior",
    category: "Extensions",
  },
  {
    id: 5,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/d5546g9j_cupboards.jpg",
    alt: "Bespoke cupboards",
    category: "Joinery",
  },
  {
    id: 6,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/qxkszh4u_roof.jpg",
    alt: "Roof tiling work",
    category: "Roofing",
  },
  {
    id: 7,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/9t5pfzz6_mediawall.jpg",
    alt: "Media wall installation",
    category: "Joinery",
  },
  {
    id: 8,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/k9rhg6so_mediawall2.jpg",
    alt: "Feature wall with fireplace",
    category: "Joinery",
  },
  {
    id: 9,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/olht3ntc_replaster.jpg",
    alt: "Plastering work",
    category: "Building",
  },
  {
    id: 10,
    src: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/lnku7fmw_office.jpg",
    alt: "Commercial fit out",
    category: "Commercial",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4 },
  },
};

export const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? GALLERY_IMAGES.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(GALLERY_IMAGES[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === GALLERY_IMAGES.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(GALLERY_IMAGES[newIndex]);
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-mono text-xs text-royal uppercase tracking-widest block mb-4">
            Our Work
          </span>
          <h2
            data-testid="gallery-heading"
            className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6"
          >
            Project Gallery
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Browse through some of our recent projects. From extensions to kitchens, 
            see the quality craftsmanship we bring to every job.
          </p>
        </div>

        {/* Gallery Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          {GALLERY_IMAGES.map((image, index) => (
            <motion.div
              key={image.id}
              variants={imageVariants}
              data-testid={`gallery-image-${image.id}`}
              className="relative aspect-square overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(image, index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors duration-300" />
              <div className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white text-sm font-medium">{image.category}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View More CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-500 text-sm">
            More photos coming soon
          </p>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/95"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              data-testid="lightbox-close"
              className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
              aria-label="Close lightbox"
            >
              <X size={24} />
            </button>

            {/* Previous Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              data-testid="lightbox-prev"
              className="absolute left-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
              aria-label="Previous image"
            >
              <ChevronLeft size={32} />
            </button>

            {/* Image */}
            <motion.img
              key={selectedImage.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              data-testid="lightbox-next"
              className="absolute right-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
              aria-label="Next image"
            >
              <ChevronRight size={32} />
            </button>

            {/* Image Info */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
              <p className="text-white font-medium">{selectedImage.alt}</p>
              <p className="text-slate-400 text-sm font-mono">
                {currentIndex + 1} / {GALLERY_IMAGES.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
